import { Order, ShipmentMinimal } from '../../../types/api'
import { PickAndPackStatus } from '../types'
import { PackableItem } from './types'

export function getShipmentItem(shipment: unknown) {
  // First check if shipment is a valid object
  if (!shipment || typeof shipment !== 'object') {
    return null
  }

  // Second check the shipment ID
  if (!('id' in shipment)) {
    return null
  }

  const id = Number(shipment.id)
  if (Number.isNaN(id)) {
    return null
  }

  // Third check the shipping box ID
  if (!('shippingBoxId' in shipment)) {
    return null
  }

  const shippingBoxId = Number(shipment.shippingBoxId)
  if (Number.isNaN(shippingBoxId)) {
    return null
  }

  // Fourth check the order item IDs
  if (!('orderItemIds' in shipment)) {
    return null
  }

  const { orderItemIds } = shipment
  if (!Array.isArray(orderItemIds) || !orderItemIds.every(orderItemId => typeof orderItemId === 'number')) {
    return null
  }

  return {
    id,
    shippingBoxId,
    orderItemIds
  }
}

export function packableItemListFromOrder(order: Order, packedItemIds: PackableItem['id'][]): PackableItem[] {
  // Maintain the initial picked status so we can keep track of the Not found status.
  // Also maintain previous status for (single) undo support
  return order.orderItems.map(item => {
    const isPacked = packedItemIds.includes(item.id)

    return {
      ...item,
      pickedStatus: isPacked ? PickAndPackStatus.PACKED : item.pickedStatus,
      packedQuantity: isPacked ? 1 : 0,
      previousPickedStatus: item.pickedStatus as PickAndPackStatus
    }
  })
}

export function resetPackableItemListFromOrder(order: Order): PackableItem[] {
  return order.orderItems.map(item => {
    return {
      ...item,
      packedQuantity: 0,
      pickedStatus: null
      // The OrderItem could have a pickedStatus of PICKED, and this would seem dangerous.
      // But we don't allow submitting this pickedStatus (they have to change it to PACKED or EXCLUDED_FROM_PACKING).
    }
  })
}

export function selectedBoxesListFromShipments(shipments: ShipmentMinimal[]) {
  return shipments.map(shipment => {
    return {
      id: shipment.shippingBoxId === -1 ? 'custom' : shipment.shippingBoxId,
      shipmentId: shipment.id,
      orderItemIds: shipment.orderItemIds,
      key: shipment.id
    }
  })
}

export function isItemPacked(item: Pick<PackableItem, 'pickedStatus'>): boolean {
  return [PickAndPackStatus.EXCLUDED_FROM_PACKING, PickAndPackStatus.PACKED].includes(
    item.pickedStatus as PickAndPackStatus
  )
}

// Release focus back to the body (to allow for scanning)
export function blurActiveInput() {
  ;(document.activeElement as HTMLElement)?.blur()
}
